import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare const grecaptcha: any;

/**
 * reCAPTCHA v3 (invisível, por score) com fallback v2 (checkbox).
 *
 * Desenhado para ser inerte quando não configurado: sem `recaptchaSiteKey` no environment o
 * script nem chega a ser baixado e `executar()` devolve null, de modo que o interceptor não
 * manda header nenhum e a aplicação se comporta exatamente como antes. É isso que permite
 * publicar este código antes de ter as chaves, e ligar app por app.
 *
 * Nenhuma falha aqui pode derrubar uma requisição: tudo que der errado vira null, e quem decide
 * se a ausência de token bloqueia é o backend (RECAPTCHA_ACOES_OBRIGATORIAS).
 */
@Injectable({ providedIn: 'root' })
export class CaptchaService {
  private carregamento: Promise<boolean> | null = null;
  private desafioEmAndamento: Promise<string | null> | null = null;

  private get siteKey(): string {
    return (environment as any).recaptchaSiteKey || '';
  }

  private get siteKeyV2(): string {
    return (environment as any).recaptchaSiteKeyV2 || '';
  }

  get habilitado(): boolean {
    return !!this.siteKey;
  }

  /** Se dá para oferecer o desafio de checkbox quando o score do v3 não passar. */
  get desafioDisponivel(): boolean {
    return !!this.siteKeyV2;
  }

  /** Tempo máximo esperando o Google. Estourou, segue sem token. */
  private static readonly TIMEOUT_MS = 10000;

  /** Começa a baixar o script na abertura do app, para a primeira API não ir sem token. */
  precarregar(): void {
    if (this.habilitado) {
      void this.carregarScript();
    }
  }

  /**
   * Devolve um token para a ação, ou null quando o captcha está desligado ou indisponível.
   * A ação precisa ser idêntica à constante usada no backend (AcoesCaptcha).
   */
  async executar(acao: string): Promise<string | null> {
    if (!this.habilitado) {
      return null;
    }

    try {
      const carregou = await this.comTimeout(this.carregarScript());
      if (!carregou) {
        return null;
      }

      return await this.comTimeout(new Promise<string | null>((resolve) => {
        // grecaptcha.ready só dispara depois que o SDK terminou de subir.
        grecaptcha.ready(() => {
          grecaptcha
            .execute(this.siteKey, { action: acao })
            .then((token: string) => resolve(token || null))
            .catch(() => resolve(null));
        });
      }));
    } catch {
      return null;
    }
  }

  /**
   * Nunca deixa uma promise do Google pendurada.
   *
   * Sem isto, um `grecaptcha.ready` que não dispara — script servido pela metade, CSP estranha,
   * rede que aceita a conexão e não responde — deixaria a requisição parada para sempre, e o
   * usuário veria a tela de login travada. O captcha é uma proteção adicional: se ele não
   * responde a tempo, o certo é seguir sem token e deixar o backend decidir.
   */
  private comTimeout<T>(promessa: Promise<T>): Promise<T | null> {
    return new Promise<T | null>((resolve) => {
      const limite = setTimeout(() => resolve(null), CaptchaService.TIMEOUT_MS);

      promessa
        .then(valor => { clearTimeout(limite); resolve(valor); })
        .catch(() => { clearTimeout(limite); resolve(null); });
    });
  }

  /**
   * Abre o desafio "Não sou um robô" e resolve com o token quando o usuário o completa.
   * Devolve null se ele fechar sem resolver, ou se o v2 não estiver configurado.
   *
   * Existe para o caso do usuário legítimo que tirou score baixo no v3: sem isto, ele levaria
   * um erro sem ter como provar que é humano.
   */
  async desafioV2(): Promise<string | null> {
    if (!this.desafioDisponivel) {
      return null;
    }

    // Duas requisições reprovadas ao mesmo tempo não podem abrir dois modais.
    if (this.desafioEmAndamento) {
      return this.desafioEmAndamento;
    }

    this.desafioEmAndamento = this.abrirDesafio().finally(() => {
      this.desafioEmAndamento = null;
    }) as Promise<string | null>;

    return this.desafioEmAndamento;
  }

  private async abrirDesafio(): Promise<string | null> {
    try {
      // Só o carregamento do script tem limite. A espera pelo clique do usuário não pode ter:
      // ele precisa de tempo para resolver o desafio.
      const carregou = await this.comTimeout(this.carregarScript());
      if (!carregou) {
        return null;
      }

      return await new Promise<string | null>((resolve) => {
        const fundo = document.createElement('div');
        fundo.setAttribute('style', [
          // Abaixo dos 2000000000 que o Google usa no overlay do desafio de imagens. Acima
          // disso, este fundo cobre o iframe do desafio: o usuario marca "Nao sou um robo",
          // o desafio abre atras e nenhum clique nos quadrados chega nele — a caixa nunca
          // fecha.
          'position:fixed', 'inset:0', 'z-index:1999999999',
          'background:rgba(0,0,0,.5)',
          'display:flex', 'align-items:center', 'justify-content:center',
        ].join(';'));

        const caixa = document.createElement('div');
        caixa.setAttribute('style', [
          'background:#fff', 'border-radius:8px', 'padding:24px',
          'box-shadow:0 8px 32px rgba(0,0,0,.3)',
          'font-family:system-ui,-apple-system,sans-serif',
          'max-width:calc(100vw - 32px)',
        ].join(';'));

        const texto = document.createElement('p');
        texto.textContent = 'Confirme que você não é um robô para continuar.';
        texto.setAttribute('style', 'margin:0 0 16px;font-size:14px;color:#333');

        const alvo = document.createElement('div');

        const cancelar = document.createElement('button');
        cancelar.type = 'button';
        cancelar.textContent = 'Cancelar';
        cancelar.setAttribute('style', [
          'margin-top:16px', 'width:100%', 'padding:8px',
          'border:1px solid #ccc', 'border-radius:4px',
          'background:#fff', 'cursor:pointer', 'font-size:14px',
        ].join(';'));

        caixa.appendChild(texto);
        caixa.appendChild(alvo);
        caixa.appendChild(cancelar);
        fundo.appendChild(caixa);
        document.body.appendChild(fundo);

        let encerrado = false;
        const encerrar = (token: string | null) => {
          if (encerrado) { return; }
          encerrado = true;
          if (fundo.parentNode) { fundo.parentNode.removeChild(fundo); }
          resolve(token);
        };

        cancelar.onclick = () => encerrar(null);

        grecaptcha.ready(() => {
          try {
            // O sitekey é explícito, então o widget v2 convive com o script carregado para o v3.
            grecaptcha.render(alvo, {
              sitekey: this.siteKeyV2,
              callback: (token: string) => encerrar(token || null),
              'expired-callback': () => encerrar(null),
              'error-callback': () => encerrar(null),
            });
          } catch {
            encerrar(null);
          }
        });
      });
    } catch {
      return null;
    }
  }

  /** Injeta o script do Google uma única vez, na primeira chamada que precisar dele. */
  private carregarScript(): Promise<boolean> {
    if (this.carregamento) {
      return this.carregamento;
    }

    this.carregamento = new Promise<boolean>((resolve) => {
      const marcarPronto = () => {
        if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
          grecaptcha.ready(() => resolve(true));
          return true;
        }
        return false;
      };

      if (marcarPronto()) {
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(this.siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // onload sozinho não basta: o global grecaptcha às vezes ainda não existe.
        if (!marcarPronto()) {
          resolve(false);
        }
      };

      // Rede bloqueada, adblock, CSP: segue sem token em vez de travar a tela.
      script.onerror = () => resolve(false);

      document.head.appendChild(script);
    });

    return this.carregamento;
  }
}
